import { useEffect, useState, useCallback } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getAllNotices } from '../../api/noticeApi';

const CATEGORY_COLORS = { General: '#94A3B8', Incentive: '#F59E0B', News: '#1A8CFF', Update: '#10B981' };

export default function DashboardSlideshow() {
    const [slides, setSlides] = useState([]);
    const [current, setCurrent] = useState(0);

    const fetchSlides = useCallback(async () => {
        try {
            const res = await getAllNotices();
            const slideshowItems = res.data.filter(n => n.type === 'Slideshow' || n.showInSlideshow);
            setSlides(slideshowItems);
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { fetchSlides(); }, [fetchSlides]);

    // Auto-advance every 5 seconds
    useEffect(() => {
        if (slides.length <= 1) return;
        const timer = setInterval(() => {
            setCurrent(prev => (prev + 1) % slides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [slides.length]);

    if (slides.length === 0) return null;

    const slide = slides[current];
    const imageUrl = slide.slideshowImageUrl || slide.attachmentUrl;
    const hasImage = !!imageUrl;

    return (
        <Box sx={{
            position: 'relative', borderRadius: '14px', overflow: 'hidden', mb: 3,
            background: hasImage ? 'none' : 'linear-gradient(135deg, #0C1B3A, #1A3A6B)',
            // Fill the column width; scale the height up on larger screens (was capped
            // at 280, which looked tiny once the dashboard went full-width). Mobile
            // stays compact. Suggested: ~200px phone, ~480px desktop.
            width: '100%', aspectRatio: '16/9', maxHeight: { xs: 200, sm: 320, md: 480 },
            display: 'flex', alignItems: 'center',
        }}>
            {/* Background image */}
            {hasImage && (
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundImage: `url(${imageUrl})`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    '&::after': {
                        content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
                    }
                }} />
            )}

            {/* Content */}
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 1, p: 2, pb: slides.length > 1 ? 4 : 2 }}>
                {slide.content && (
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', fontFamily: "'Poppins', sans-serif", maxWidth: 500, lineHeight: 1.4 }}>
                        {slide.content.length > 120 ? slide.content.substring(0, 120) + '...' : slide.content}
                    </Typography>
                )}
            </Box>

            {/* Navigation arrows */}
            {slides.length > 1 && (
                <>
                    <IconButton onClick={() => setCurrent(prev => (prev - 1 + slides.length) % slides.length)}
                        sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <IconButton onClick={() => setCurrent(prev => (prev + 1) % slides.length)}
                        sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                        <ChevronRightIcon />
                    </IconButton>

                    {/* Dots */}
                    <Box sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 0.75 }}>
                        {slides.map((_, i) => (
                            <Box key={i} onClick={() => setCurrent(i)}
                                sx={{
                                    width: i === current ? 20 : 8, height: 8, borderRadius: 4, cursor: 'pointer',
                                    backgroundColor: i === current ? '#1A8CFF' : 'rgba(255,255,255,0.4)',
                                    transition: 'all 0.3s',
                                }} />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
}
